/**
 * PanelOperationsMixin — PanelOperationsService 퍼사드 wrapper
 */
import { createPanelOperationsService } from '../services/panelOperationsService.js';
import { getUniqueStringSeqsFromSelectionFeatures } from '../utils/panelMapSelection';
import { normalizeEntityRenameName } from '../utils/panelEntityNumbering.js';
import {
  buildPanelMoveNumberingPlan,
  resolvePanelMoveDisplayName
} from '../utils/panelMoveNumberingPlan.js';
import { panelPointerMovePerfMarkEvent } from '../utils/panelPointerMovePerfLog.js';
import { pickRememberedMoveTargetId } from '../utils/panelMoveTargetMemory.js';
import { summarizeTreeBulkSelection } from '../utils/panelTreeBulkSelection.js';
import { resolveSolarModuleDeleteKeyFromTreeItem } from '../utils/flattenModuleTreeRows.js';

export const opsSetup = {
  data() {
    return {
      lastMoveTargetArrayId: null,
      lastMoveTargetStringId: null
    };
  },

  computed: {
    _opsSvc() { return createPanelOperationsService(); }
  },

  watch: {
    'renameDialog.show'(isOpen) {
      if (isOpen) {
        this.scheduleRenameInputSelectAll();
      }
    }
  },

  methods: {
    scheduleRenameInputSelectAll() {
      this.$nextTick(() => {
        requestAnimationFrame(() => {
          this.focusRenameInputSelectAll();
        });
      });
    },

    focusRenameInputSelectAll() {
      const field = this.$refs.renameNameInput;
      if (!field) {
        return;
      }
      if (typeof field.focus === 'function') {
        field.focus();
      }
      const input = field.$refs?.input || field.$el?.querySelector?.('input');
      if (input) {
        input.focus();
        if (typeof input.select === 'function') {
          input.select();
        }
      }
    },

    showRenameDialog() {
      if (!this.contextMenu?.item) return;
      this.renameDialog.item = this.contextMenu.item;
      this.renameDialog.newName = this._opsSvc.stripNamePrefix(this.contextMenu.item.name);
      this.renameDialog.show = true;
      this.contextMenu.show = false;
    },

    async confirmRename() {
      if (!this.renameDialog.item || !this.renameDialog.newName) return;
      const { item, newName } = this.renameDialog;
      const trimmed = newName.trim();
      const canonicalName = normalizeEntityRenameName(item.type, trimmed);
      if (!canonicalName || canonicalName === item.name) {
        this.renameDialog.show = false;
        return;
      }

      await this.handleNameChanged?.({
        itemId: this.resolveRenameApiItemId?.(item),
        nodeId: item.id,
        oldName: item.name,
        newName: canonicalName,
        siteId: this.selectedSite?.id,
        itemType: item.type
      });
      this.applyLocalRenameAfterContextMenu?.(item, canonicalName);
      this.renameDialog.show = false;
      this.renameDialog.item = null;
      this.renameDialog.newName = '';
    },

    showMoveDialog() {
      const item = this.contextMenu?.item;
      if (!item || (item.type !== 'string' && item.type !== 'module')) return;
      this.openMoveDialog(item);
      this.contextMenu.show = false;
    },

    openMoveDialog(item, { fromBulkHeader = false } = {}) {
      if (item?.type === 'module') {
        this.moveDialog.moduleItems = fromBulkHeader
          ? this.getTreeBulkSelectedItems?.().filter(node => node.type === 'module')
          : [item];
        this.moveDialog.item = this.moveDialog.moduleItems[0] ?? item;
        this.moveDialog.stringItems = [];
      } else if (item?.type === 'string') {
        this.moveDialog.stringItems = fromBulkHeader
          ? this.getTreeBulkSelectedItems?.().filter(node => node.type === 'string')
          : this.resolveStringItemsForMove(item);
        this.moveDialog.item = this.moveDialog.stringItems[0] ?? item;
        this.moveDialog.moduleItems = [];
      } else {
        return;
      }
      this.moveDialog.targetArrayId = null;
      this.moveDialog.targetStringId = null;
      this.moveDialog.appendAndRenumber = false;
      this.moveDialog.show = true;
      this.$nextTick(() => {
        this.applyRememberedMoveTargets();
      });
    },

    applyRememberedMoveTargets() {
      const itemType = this.moveDialog.item?.type;
      if (itemType === 'string') {
        const remembered = pickRememberedMoveTargetId(
          this.availableArrays,
          this.lastMoveTargetArrayId
        );
        if (remembered != null) {
          this.moveDialog.targetArrayId = remembered;
        }
        return;
      }
      if (itemType === 'module') {
        const remembered = pickRememberedMoveTargetId(
          this.availableStringsForModuleMove,
          this.lastMoveTargetStringId
        );
        if (remembered != null) {
          this.moveDialog.targetStringId = remembered;
        }
      }
    },

    /**
     * 지도 다중 선택(Shift 등) > 앵커 String 순으로 이동 대상 String 목록 resolve
     */
    resolveStringItemsForMove(anchorItem) {
      const seqs = getUniqueStringSeqsFromSelectionFeatures(this.groupSelection?.getArray() ?? []);
      if (seqs.length > 1) {
        return seqs
          .map(seq => this.resolveStringTreeItemForDelete?.(seq))
          .filter(Boolean);
      }
      if (anchorItem?.type === 'string') {
        return [anchorItem];
      }
      if (seqs.length === 1) {
        const one = this.resolveStringTreeItemForDelete?.(seqs[0]);
        return one ? [one] : [];
      }
      return [];
    },

    async confirmMove() {
      const {
        item,
        targetArrayId,
        targetStringId,
        stringItems,
        appendAndRenumber
      } = this.moveDialog;
      const numberingMode = appendAndRenumber ? 'append-renumber' : 'preserve';
      if (!item) return;
      if (item.type === 'string' && targetArrayId != null && targetArrayId !== '') {
        const items = stringItems?.length ? stringItems : [item];
        return this._confirmMoveStrings(items, targetArrayId, numberingMode);
      }
      if (item.type === 'module' && targetStringId != null && targetStringId !== '') {
        const items =
          this.moveDialog.moduleItems?.length > 0 ? this.moveDialog.moduleItems : [item];
        return this._confirmMoveModules(items, targetStringId, numberingMode);
      }
    },

    async _confirmMoveStrings(items, targetArrayId, numberingMode = 'preserve') {
      const list = (items || []).filter(i => i?.type === 'string');
      if (!list.length) return;

      try {
        const targetArray = this._opsSvc.resolveArraySeqFromTreeItem(
          this.treeData, targetArrayId, this.resolveArraySeqFromTreeItem
        );
        if (!targetArray) {
          throw new Error(this.$t('panelPlanning.stringMoveFailedMsg', { message: 'Target not found' }));
        }

        const targetSeq = String(this.resolveArraySeqFromTreeItem?.(targetArray) ?? targetArrayId);
        const toMove = list.filter(stringItem => {
          const parentArray = this.findParentArrayNodeForString?.(stringItem.id);
          const parentSeq = parentArray ? String(this.resolveArraySeqFromTreeItem?.(parentArray) ?? '') : '';
          return parentSeq !== targetSeq;
        });

        this.lastMoveTargetArrayId = targetArrayId;

        if (!toMove.length) {
          this.closeMoveDialog();
          this.$emit('show-message', {
            type: 'info',
            text: this.$t('panelPlanning.stringsAlreadyInTargetArray')
          });
          return;
        }

        const movingSeqs = new Set(
          toMove
            .map(stringItem => this.resolveStringSeqFromTreeItem?.(stringItem))
            .filter(seq => seq != null)
            .map(seq => String(seq))
        );
        const targetItems = (targetArray.children || []).filter(
          stringItem =>
            !movingSeqs.has(
              String(this.resolveStringSeqFromTreeItem?.(stringItem) ?? stringItem.seq ?? stringItem.id)
            )
        );
        const numberingPlan = buildPanelMoveNumberingPlan({
          entityType: 'string',
          movingItems: toMove,
          targetItems,
          mode: numberingMode
        });
        const assignmentBySeq = new Map(
          numberingPlan.assignments.map(assignment => [String(assignment.entitySeq), assignment])
        );

        for (const stringItem of toMove) {
          const stringSeq = this.resolveStringSeqFromTreeItem?.(stringItem);
          const assignment = assignmentBySeq.get(String(stringSeq));
          const displayUpdate = assignment
            ? {
                ...assignment,
                name: resolvePanelMoveDisplayName(
                  'string',
                  stringItem.name,
                  assignment.displayNo
                )
              }
            : null;
          await this.handleNodeMoved({
            sourceItem: stringItem,
            targetItem: targetArray,
            sourceGroupName: 'string',
            targetGroupName: 'array',
            numberingAssignment: displayUpdate
          });
          this.moveStringFeaturesToNewArrayOnMap?.(stringItem, targetArray, displayUpdate);
          if (stringSeq != null && displayUpdate) {
            this.syncEntityRenameInLocalDesign?.(
              'string',
              stringSeq,
              displayUpdate.name,
              {
                treeNodeId: stringItem.id,
                displayId: displayUpdate.displayNo
              }
            );
          }
        }

        this.clearGroupMapSelection?.();
        this.closeMoveDialog();
        this.rebuildPanelMapFeatureIndex?.();
        this.requestVectorStyleRefresh?.();

        panelPointerMovePerfMarkEvent('string-move-dialog-complete', {
          movedCount: toMove.length,
          targetArrayId: targetArray.id,
          targetArrayName: targetArray.name
        });

        const message =
          toMove.length > 1
            ? this.$t('panelPlanning.stringsMovedSuccess', { count: toMove.length, array: targetArray.name })
            : this.$t('panelPlanning.stringMovedSuccess', { name: toMove[0].name });
        this.$emit('show-message', { type: 'success', text: message });
      } catch (e) {
        await this.reloadPanelPlanningAfterMoveFailure?.();
        this.$emit('show-message', { type: 'error', text: e?.message || 'Move failed' });
      }
    },

    async _confirmMoveString(item, targetArrayId) {
      return this._confirmMoveStrings([item], targetArrayId);
    },

    async _confirmMoveModule(item, targetStringId) {
      return this._confirmMoveModules([item], targetStringId);
    },

    async _confirmMoveModules(items, targetStringId, numberingMode = 'preserve') {
      const list = (items || []).filter(i => i?.type === 'module');
      if (!list.length) return;
      const appliedSnapshots = [];
      try {
        const targetString = this.findStringTreeItemInTreeData?.(this.treeData, targetStringId);
        if (!targetString) throw new Error('Target string not found');
        this.lastMoveTargetStringId = targetStringId;
        const toMove = list.filter(moduleItem => {
          const parent = this.findParentStringNodeForModule?.(moduleItem.id);
          return parent?.id !== targetString.id;
        });
        if (!toMove.length) {
          this.closeMoveDialog();
          this.$emit('show-message', {
            type: 'info',
            text: this.$t('panelPlanning.modulesAlreadyInTargetString')
          });
          return;
        }

        const targetStringSeq = this.resolveStringSeqFromTreeItem?.(targetString);
        const targetStringInfo =
          this.flattenedSiteDesign?.byString?.get(targetStringSeq) ??
          this.flattenedSiteDesign?.byString?.get(Number(targetStringSeq)) ??
          this.flattenedSiteDesign?.byString?.get(String(targetStringSeq));
        const targetItems =
          targetStringInfo?.solarModules ||
          targetStringInfo?.children ||
          targetString.children ||
          [];
        const resolveModuleSeq = moduleItem =>
          resolveSolarModuleDeleteKeyFromTreeItem(
            moduleItem?.type ? moduleItem : { ...moduleItem, type: 'module' },
            { flattenedSiteDesign: this.flattenedSiteDesign }
          );
        const movingModuleSeqs = new Set(
          toMove
            .map(resolveModuleSeq)
            .filter(seq => seq != null)
            .map(seq => String(seq))
        );
        const existingTargetItems = targetItems.filter(moduleItem => {
          const moduleSeq = resolveModuleSeq(moduleItem);
          return !movingModuleSeqs.has(String(moduleSeq));
        });
        const numberingPlan = buildPanelMoveNumberingPlan({
          entityType: 'module',
          movingItems: toMove,
          targetItems: existingTargetItems,
          mode: numberingMode
        });
        const assignmentBySeq = new Map(
          numberingPlan.assignments.map(assignment => [String(assignment.entitySeq), assignment])
        );

        for (const moduleItem of toMove) {
          const moduleSeq = resolveModuleSeq(moduleItem);
          const assignment = assignmentBySeq.get(String(moduleSeq));
          const displayUpdate = assignment
            ? {
                ...assignment,
                name: resolvePanelMoveDisplayName(
                  'module',
                  moduleItem.name,
                  assignment.displayNo
                )
              }
            : null;
          const result = await this.executeOptimisticModuleMove?.(
            moduleItem,
            targetString,
            displayUpdate
          );
          if (result?.snapshot) {
            appliedSnapshots.push(result.snapshot);
          }
        }
        this.clearGroupMapSelection?.();
        this.closeMoveDialog();
        const message =
          toMove.length > 1
            ? this.$t('panelPlanning.modulesMovedSuccess', {
                count: toMove.length,
                string: targetString.name
              })
            : this.$t('panelPlanning.moduleMovedSuccess', { name: toMove[0].name });
        this.$emit('show-message', { type: 'success', text: message });
      } catch (e) {
        for (const snapshot of appliedSnapshots.reverse()) {
          this.rollbackOptimisticModuleMove?.(snapshot);
        }
        await this.reloadPanelPlanningAfterMoveFailure?.();
        this.$emit('show-message', { type: 'error', text: e?.message || 'Move failed' });
      }
    },

    async reloadPanelPlanningAfterMoveFailure() {
      if (!this.selectedSite?.id || typeof this.selectSiteAndLoadDesign !== 'function') {
        return false;
      }
      await this.selectSiteAndLoadDesign(this.selectedSite);
      return true;
    },

    closeMoveDialog() {
      this.moveDialog.show = false;
      this.moveDialog.item = null;
      this.moveDialog.stringItems = [];
      this.moveDialog.moduleItems = [];
      this.moveDialog.targetArrayId = null;
      this.moveDialog.targetStringId = null;
      this.moveDialog.appendAndRenumber = false;
    },

    async confirmDelete() {
      const item = this.contextMenu?.item;
      if (!item) return;
      if (item.type === 'string') return this._confirmDeleteString(item);
      if (item.type === 'module') return this.confirmDeleteModuleFromTree?.(item);
      if (item.type === 'array') return this._confirmDeleteArray(item);
    },

    async _confirmDeleteString(item) {
      if (!item) return;
      await this.confirmDeleteStringFromTree?.(item);
    },

    async _confirmDeleteArray(item) {
      if (!item) return;
      await this.confirmDeleteArrayFromTree?.(item);
    },

    handleRenameRequested(data) {
      this.renameDialog.item = data.item;
      this.renameDialog.newName = this._opsSvc.stripNamePrefix(data.item.name);
      this.renameDialog.show = true;
    },

    handleMoveRequested(data) {
      const { item } = data;
      if (!item || (item.type !== 'string' && item.type !== 'module')) return;
      this.openMoveDialog(item);
    },

    openTreeBulkMoveDialog() {
      const items = this.getTreeBulkSelectedItems?.() ?? [];
      const summary = summarizeTreeBulkSelection(items);
      if (!summary.canMove) return;
      this.openMoveDialog(items[0], { fromBulkHeader: true });
    },

    handleDeleteRequested(data) {
      const { item } = data;
      if (!item) return;
      if (item.type === 'string') return this._confirmDeleteString(item);
      if (item.type === 'module') return this.confirmDeleteModuleFromTree?.(item);
      if (item.type === 'array') return this._confirmDeleteArray(item);
    },

    async onTreeNameChanged(payload) {
      try { await this.handleNameChanged?.(payload); }
      catch (e) { this.$emit('show-message', { type: 'error', text: e?.message }); return; }
      this.$store.commit('panel/moduleTree/RENAME_TREE_NODE', {
        nodeId: payload.nodeId || payload.itemId, newName: payload.newName
      });
      if (payload.itemType === 'string') this.refreshMapLabelsAfterTreeChange?.();
    },

    refreshMapLabelsAfterTreeChange() {
      this.rebuildPanelMapFeatureIndex?.();
      this.syncModuleLabelVisibilityAfterFeaturesChange?.();
      this.map?.render();
    },

    deleteSolarString(seq) {
      return this._opsSvc.deleteSolarString(this.selectedSite?.id, seq);
    },

    deleteStringWithAllModules() { /* no-op: API cascade */ },
    deleteSolarStringApi(siteId, seq) { return this._opsSvc.deleteSolarString(siteId, seq); },

    removeStringFromDerivedTree(nodeId) {
      this.$store.commit('panel/moduleTree/REMOVE_STRING_TREE_NODE', nodeId);
    },

    findParentArrayNodeForString(stringNodeId) {
      if (!stringNodeId || !this.treeData?.length) return null;
      const target = String(stringNodeId);
      for (const arrayNode of this.treeData) {
        if (arrayNode.type !== 'array' || !arrayNode.children?.length) continue;
        if (arrayNode.children.some(child => String(child.id) === target)) {
          return arrayNode;
        }
      }
      return null;
    }
  }
};
